import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {loadStripe} from '@stripe/stripe-js/pure';
import type {Stripe} from '@stripe/stripe-js';
import {Alert, AlertTitle, Container} from '@mui/material';
import {FilingAvailability, loadFilingAvailability} from '../assets/filing-availability';
import {recordPageVisit} from '../assets/page-visit';
import Loading from './loading';
import NoFurtherRenewals from './no-further-renewals';
import ResponsiveAppBar from './responsive-app-bar';
import Footer from './home/Footer';
import {DarkButton} from './buttons';
import {PageErrorBoundary} from './page-error-boundary';

const FilingStripeContext = createContext<Promise<Stripe | null> | null>(null);
const FilingSettingsContext = createContext<FilingAvailability | null>(null);

export function useFilingStripe() {
    return useContext(FilingStripeContext);
}

/** Nested inside PublicFilingGate: closes annual-report (renewal) pages while other filings stay open. */
export function AnnualReportGate() {
    const settings = useContext(FilingSettingsContext);
    return settings?.annualReportsEnabled ? <Outlet/> : <NoFurtherRenewals annualReportsOnly/>;
}

/** Shown when the availability check fails, which is not the same as filings being closed. */
function ServerUnreachable() {
    return <>
        <ResponsiveAppBar showLogin={false}/>
        <Container maxWidth="sm" sx={{py: 6}}>
            <Alert severity="warning">
                <AlertTitle>We couldn't reach our server</AlertTitle>
                Please refresh the page in a moment. If this keeps happening, contact support
                at <a href="mailto:support@fcfiling.com">support@fcfiling.com</a>.
            </Alert>
            <DarkButton variant="contained" disableElevation sx={{mt: 3}} onClick={() => window.location.reload()}>Refresh</DarkButton>
        </Container>
        <Footer/>
    </>;
}

function FilingAvailabilityBoundary({children}: {children: React.ReactNode}) {
    const [settings, setSettings] = useState<FilingAvailability | 'unreachable' | null>(null);
    const available = settings === 'unreachable' ? null : settings;
    const stripe = useMemo(() => available?.enabled && available.stripePublishableKey
        ? loadStripe(available.stripePublishableKey) : null, [available?.enabled, available?.stripePublishableKey]);

    useEffect(() => {
        let active = true;
        let pending: AbortController | undefined;
        const refresh = async () => {
            pending?.abort();
            const controller = new AbortController();
            pending = controller;
            try {
                const next = await loadFilingAvailability(controller.signal);
                if (active && pending === controller) setSettings(next);
            } catch {
                // A failed check never closes the page; it only matters while nothing has loaded yet.
                if (active && pending === controller) setSettings(current => current === null ? 'unreachable' : current);
            }
        };
        void refresh();
        const interval = window.setInterval(refresh, 60000);
        window.addEventListener('focus', refresh);
        return () => {
            active = false;
            pending?.abort();
            window.clearInterval(interval);
            window.removeEventListener('focus', refresh);
        };
    }, []);

    if (settings === null) return <Loading/>;
    if (settings === 'unreachable') return <ServerUnreachable/>;
    if (!settings.enabled) return <NoFurtherRenewals/>;
    return <FilingSettingsContext.Provider value={settings}>
        <FilingStripeContext.Provider value={stripe}>{children}</FilingStripeContext.Provider>
    </FilingSettingsContext.Provider>;
}

export default function PublicFilingGate() {
    const {pathname, search} = useLocation();
    // Recorded before availability is known, so visits to a closed page still count.
    useEffect(() => recordPageVisit(pathname, search), [pathname, search]);
    return <FilingAvailabilityBoundary key={pathname}><PageErrorBoundary><Outlet/></PageErrorBoundary></FilingAvailabilityBoundary>;
}
